import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { resolveMedia, isVideoUrl } from "@/lib/api";
import { SectionTitle } from "@/components/SectionTitle";
import { ArrowRight, Trophy, Crown, Fire, CalendarBlank, Megaphone, X } from "@phosphor-icons/react";

function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || "").trim());
  if (!m) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function HeroCta({ text, link, primary }) {
  const label = (text || "").trim();
  if (!label) return null;
  const to = (link || "").trim() || "/";
  const cls = primary
    ? "bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-8 py-4 flex items-center gap-2 transition-all duration-200"
    : "border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-8 py-4 transition-all duration-200";
  const testid = primary ? "hero-primary-cta" : "hero-secondary-cta";
  const inner = primary ? (<>{label} <ArrowRight size={20} weight="bold" /></>) : label;
  if (/^https?:\/\//i.test(to)) {
    return <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>{inner}</a>;
  }
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
}

export function HeroSection({ heroImages, slideshowOn, fitClass, announcements, hero = {} }) {
  const [slide, setSlide] = useState(0);

  const kicker = [hero.season_title, hero.status_text].map((s) => (s || "").trim()).filter(Boolean).join(" · ");
  const tagline = (hero.tagline || "").trim() || "Where the city comes to ball.";
  const overlay = hero.overlay_color || "#000000";

  useEffect(() => {
    if (!slideshowOn) { setSlide(0); return; }
    const t = setInterval(() => setSlide((s) => (s + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, [slideshowOn, heroImages.length]);

  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden">
      {heroImages.map((img, i) => {
        const active = i === (slideshowOn ? slide : 0);
        return isVideoUrl(img) ? (
          <video
            key={`${img}-${i}`}
            src={resolveMedia(img)}
            autoPlay
            muted
            loop
            playsInline
            className={`absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-1000`}
            style={{ opacity: active ? 1 : 0 }}
          />
        ) : (
          <img
            key={`${img}-${i}`}
            src={resolveMedia(img)}
            alt="basketball"
            className={`absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-1000`}
            style={{ opacity: active ? 1 : 0 }}
          />
        );
      })}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(to right, ${hexToRgba(overlay, 1)}, ${hexToRgba(overlay, 0.8)}, ${hexToRgba(overlay, 0.4)})` }}
      />
      {slideshowOn && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroImages.map((img, i) => (
            <button
              key={`dot-${img}-${i}`}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 transition-all duration-300 ${i === slide ? "w-8 bg-[#FF5722]" : "w-3 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-6 w-full flex flex-col lg:flex-row lg:items-center justify-between gap-10">
        <div className="max-w-2xl fade-up">
          {kicker && (
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF5722] mb-5 font-semibold" data-testid="hero-kicker">
              {kicker}
            </p>
          )}
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.85]" data-testid="hero-tagline">
            {tagline}
          </h1>
          <div className="flex flex-wrap gap-4 mt-9">
            <HeroCta text={hero.primary_cta_text ?? "Register Now"} link={hero.primary_cta_link ?? "/register"} primary />
            <HeroCta text={hero.secondary_cta_text ?? "View Schedule"} link={hero.secondary_cta_link ?? "/schedule"} />
          </div>
        </div>

        {announcements.length > 0 && <AnnouncementsBox announcements={announcements} />}
      </div>
    </section>
  );
}

function AnnouncementsBox({ announcements }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <div className="w-full lg:w-[400px] shrink-0 bg-black/55 backdrop-blur-md border border-white/15 fade-up" data-testid="announcement-box">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
        <Megaphone size={20} weight="fill" className="text-[#FF5722]" />
        <span className="font-display text-2xl uppercase">Announcements</span>
      </div>
      <div className="max-h-[70vh] lg:max-h-[520px] overflow-y-auto divide-y divide-white/10">
        {announcements.map((a, i) => (
          <button
            key={`${a.url}-${i}`}
            type="button"
            onClick={() => setActive(i)}
            data-testid="announcement-item"
            className="group w-full text-left p-4 hover:bg-white/5 transition-colors duration-200 cursor-pointer"
          >
            <div className="relative bg-black overflow-hidden">
              {isVideoUrl(a.url)
                ? <video src={resolveMedia(a.url)} muted playsInline className="w-full h-[340px] object-contain pointer-events-none" />
                : <img src={resolveMedia(a.url)} alt={a.caption || `announcement ${i + 1}`} className="w-full h-[340px] object-contain" />}
              <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wide font-bold bg-black/70 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {isVideoUrl(a.url) ? "Tap to play" : "Tap to enlarge"}
              </span>
            </div>
            {a.caption && <p className="text-white/80 text-sm mt-2 line-clamp-2">{a.caption}</p>}
          </button>
        ))}
      </div>

      {active !== null && announcements[active] && (
        <AnnouncementModal item={announcements[active]} onClose={() => setActive(null)} />
      )}
    </div>
  );
}

function AnnouncementModal({ item, onClose }) {
  return createPortal((
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-black/85 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="announcement-modal"
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#121212] border border-white/15" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#121212] z-10">
          <div className="flex items-center gap-2">
            <Megaphone size={22} weight="fill" className="text-[#FF5722]" />
            <span className="font-display text-2xl uppercase">Announcement</span>
          </div>
          <button onClick={onClose} data-testid="announcement-modal-close" aria-label="Close announcement" className="flex items-center justify-center w-9 h-9 text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6">
          <div className="bg-black flex items-center justify-center">
            {isVideoUrl(item.url)
              ? <video src={resolveMedia(item.url)} controls autoPlay playsInline className="w-full max-h-[70vh] object-contain" />
              : <img src={resolveMedia(item.url)} alt={item.caption || "announcement"} className="w-full max-h-[70vh] object-contain" />}
          </div>
          {item.caption && <p className="text-white/90 text-lg leading-relaxed mt-6">{item.caption}</p>}
          <button onClick={onClose} data-testid="announcement-modal-back" className="mt-8 w-full sm:w-auto bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-8 py-3 transition-all duration-200">
            Close
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function divisionLeaders(standings) {
  const seen = new Set();
  const leaders = [];
  for (const s of standings) {
    const div = (s.division || "Open").trim() || "Open";
    if (seen.has(div)) continue;
    if ((s.gp || 0) === 0) continue;
    seen.add(div);
    leaders.push({ ...s, division: div });
  }
  return leaders;
}

export function ChampionsBanner({ standings, topScorer = null, teamName = () => "" }) {
  const leaders = divisionLeaders(standings || []);
  if (leaders.length === 0 && !topScorer) return null;

  const ppg = topScorer && topScorer.games_played > 0
    ? (topScorer.points / topScorer.games_played).toFixed(1)
    : null;

  return (
    <section className="relative overflow-hidden border-y border-[#FF5722]/30 bg-[#0A0A0A]" data-testid="champions-banner">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF5722 0, transparent 40%), radial-gradient(circle at 80% 70%, #FF5722 0, transparent 40%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-8 fade-up">
          <Crown size={28} weight="fill" className="text-[#FF5722]" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] font-semibold">Who's on top</p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none">Season Spotlight</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {topScorer && (
            <Link
              to="/stats"
              data-testid="top-scorer-card"
              className="group relative overflow-hidden border border-[#FF5722]/40 bg-gradient-to-br from-[#FF5722]/15 to-transparent p-6 flex flex-col justify-between hover:border-[#FF5722] hover:-translate-y-1 transition-all duration-200 fade-up lg:row-span-2"
            >
              <div className="flex items-center gap-2">
                <Fire size={22} weight="fill" className="text-[#FF5722]" />
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] font-bold">Top Scorer</p>
              </div>
              <div className="mt-6 lg:mt-auto">
                <p className="font-display text-4xl sm:text-5xl uppercase leading-[0.9] group-hover:text-[#FF5722] transition-colors" data-testid="top-scorer-name">{topScorer.name}</p>
                <p className="text-sm text-white/50 uppercase tracking-wide mt-1">{teamName(topScorer.team_id)}</p>
                <div className="flex items-end gap-2 mt-5">
                  <span className="font-display text-6xl sm:text-7xl text-[#FF5722] tabular-nums leading-none" data-testid="top-scorer-points">{topScorer.points}</span>
                  <span className="text-white/50 text-sm uppercase tracking-wide pb-2">pts</span>
                </div>
                {ppg && <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{ppg} per game</p>}
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:col-span-2">
            {leaders.map((s, i) => (
              <Link
                to="/standings"
                key={s.team_id}
                data-testid={`champion-card-${i}`}
                className="group relative border border-white/10 bg-[#121212] p-6 flex items-center gap-5 hover:border-[#FF5722] hover:-translate-y-1 transition-all duration-200 fade-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <div className="relative shrink-0">
                  {s.logo_url
                    ? <img src={resolveMedia(s.logo_url)} alt={s.team_name} className="w-16 h-16 object-contain" />
                    : <div className="w-16 h-16 flex items-center justify-center" style={{ backgroundColor: `${s.primary_color || "#FF5722"}22` }}><Trophy size={30} weight="fill" style={{ color: s.primary_color || "#FF5722" }} /></div>}
                  <Crown size={20} weight="fill" className="absolute -top-2 -right-2 text-[#FFB300] drop-shadow" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-semibold truncate">{s.division}</p>
                  <p className="font-display text-2xl uppercase leading-tight truncate group-hover:text-[#FF5722] transition-colors" data-testid={`champion-name-${i}`}>{s.team_name}</p>
                  <p className="text-sm text-white/60 tabular-nums mt-1">
                    <span className="text-[#00E676] font-bold">{s.w}</span>–{s.l}
                    <span className="text-white/30"> · </span>
                    <span className="text-white/70">{s.pts} PTS</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function StatsBar({ stats }) {  return (
    <section className="border-y border-white/10 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
        {stats.map((s) => (
          <div key={s.label} className="py-8 px-4 text-center" data-testid={`overview-${s.label.toLowerCase()}`}>
            <s.icon size={26} className="text-[#FF5722] mx-auto mb-3" />
            <div className="font-display text-5xl tabular-nums">{s.value}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function UpcomingGames({ games, teamName }) {
  return (
    <div className="md:col-span-7">
      <SectionTitle overline="On the schedule" title="Upcoming Games" />
      <div className="space-y-4">
        {games.length === 0 && <p className="text-white/40">No games scheduled yet. Check back soon.</p>}
        {games.map((g) => (
          <div key={g.id} className="border border-white/10 p-6 hover:-translate-y-1 hover:shadow-[0_0_12px_rgba(255,87,34,0.3)] transition-all duration-200 bg-[#121212]">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-display text-2xl uppercase">{teamName(g.home_team_id)}</div>
              </div>
              <div className="px-4 text-[#FF5722] font-display text-xl">VS</div>
              <div className="flex-1 text-right">
                <div className="font-display text-2xl uppercase">{teamName(g.away_team_id)}</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-4 text-sm text-white/50">
              <span className="flex items-center gap-1"><CalendarBlank size={16} /> {new Date(g.date).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {g.venue && <span>· {g.venue}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopTeams({ standings }) {
  return (
    <div className="md:col-span-5">
      <SectionTitle overline="The race" title="Top Teams" />
      <div className="border border-white/10 bg-[#121212]">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <Trophy size={20} weight="fill" className="text-[#FF5722]" />
          <span className="font-display text-xl uppercase">League Standings</span>
        </div>
        {standings.slice(0, 5).map((s, i) => (
          <div key={s.team_id} className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-4">
              <span className="font-display text-2xl text-[#FF5722] tabular-nums w-6">{i + 1}</span>
              <span className="font-semibold uppercase tracking-wide text-sm">{s.team_name}</span>
            </div>
            <div className="text-sm tabular-nums text-white/70">
              <span className="text-white font-bold">{s.w}</span>-{s.l}
            </div>
          </div>
        ))}
        {standings.length === 0 && <p className="text-white/40 px-5 py-6">No standings yet.</p>}
        <Link to="/standings" className="block px-5 py-4 text-[#FF5722] uppercase text-sm font-bold tracking-wide hover:bg-white/5 transition-colors">
          Full Standings →
        </Link>
      </div>
    </div>
  );
}
